<template>
  <div id="app">
    <nav class="navbar fixed-top navbar-light">
      <a class="navbar-brand d-flex align-items-center" href="/">
        <img src="./assets/logo.png" alt="Palaver" width="33" height="33" class="mr-3">
        <span class="d-none d-sm-block">Palaver</span>
        <span class="sub-title d-none d-sm-block">theme tester</span>
      </a>

      <div>
        <div class="btn-group theme-variant-switch" role="group" aria-label="Theme variant">
          <button type="button" class="btn" :class="{'btn-primary': variant == 'light'}" @click="variant = 'light'">Light</button>
          <button type="button" class="btn" :class="{'btn-primary': variant == 'dark'}" @click="variant = 'dark'">Dark</button>
        </div>

        <button type="button" class="btn btn-primary ml-3" data-toggle="modal" data-target="#exportModal">Export</button>
      </div>
    </nav>

    <Split ref="split" style="height: 100vh;" :direction="direction()">
      <SplitArea class="overflow: hidden;">
          <codemirror style="overflow: hidden;" v-model="code" :options="options" />
      </SplitArea>
      <SplitArea>
          <div class="alert alert-warning" role="alert" v-if="error != null">
            <font-awesome-icon icon="exclamation-triangle" class="mr-1" />
            {{ error.message }}
          </div>
          <preview :theme="theme" :variant="variant" />
      </SplitArea>
    </Split>

    <div class="modal fade" id="exportModal" tabindex="-1" role="dialog" aria-labelledby="exportModalLabel" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exportModalLabel">Exported theme</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body text-center">
            <img src="./assets/logo.png" alt="Palaver" width="75" height="75" class="d-block mx-auto mb-3">
            <span v-if="runningiOS">Click the link below to install your theme.</span>
            <span v-else>Open this link on your iOS device to install the theme.</span>
          </div>
          <div v-if="runningiOS" class="modal-footer d-flex justify-content-between">
            <span v-if="theme != null" class="text-muted">{{ theme.name }}</span>
            <a :href="`plv:///loadTheme/${hash}`" class="btn btn-primary">Install</a>
          </div>
          <div v-else class="modal-footer d-flex justify-content-between copy">
            <input v-if="theme != null" id="url" class="text-muted form-control" type="text" :value="`${origin}/?t=${hash}&modal`" />
            <button data-clipboard-target="#url" class="btn btn-sm btn-primary">Copy</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { codemirror } from 'vue-codemirror'
import preview from './components/Preview.vue'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/theme/base16-light.css'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import $ from 'jquery'
import pako from 'pako'
import ClipboardJS from 'clipboard'

library.add(faExclamationTriangle)

export default {
  name: 'app',
  data() {
    return {
      code: `{
  "name": "Example",

  "light": {
    "normal": {
      "message": "#464c59",
      "time": "#9fa7b7"
    },
    "action": {
      "message": "#9fa7b7"
    },
    "mention": {
      "background": "#f7f1c0",
      "time": "#9fa7b7"
    },
    "notice": {
      "background": "#d9dfeb",
      "time": "#9fa7b7"
    },
    "status": {
      "message": "#9fa7b7"
    },
    "ownNick": "#d43e62",
    "nickColors": [
      "#3b92f5"
    ]
  },

  "dark": {
    "normal": {
      "message": "#b4c0d6",
      "time": "#707c95"
    },
    "action": {
      "message": "#6b7487"
    },
    "mention": {
      "background": "#373f4c",
      "time": "#8d9bb7"
    },
    "notice": {
      "background": "#546672",
      "time": "#b4c0d6"
    },
    "status": {
      "message": "#6b7487"
    },
    "ownNick": "#d43e62",
    "nickColors": [
      "#3b92f5"
    ]
  }
}
`,
      options: {
        mode: 'application/ld+json',
        theme: 'base16-light',
        lineNumbers: true,
        foldGutter: true,
        autoCloseBrackets: true,
        matchBrackets: true,
        colorpicker : true
      },
      theme: null,
      error: null,
      hash: null,
      variant: 'light',
      origin: ''
    }
  },
  components: {
    codemirror,
    preview,
    FontAwesomeIcon
  },
  mounted() {
    const params = new URLSearchParams(window.location.search)
    
    const theme = params.get('t')
    if(theme != null) {
      this.code = pako.inflate(this.urlSafeB64Decode(theme), {to: 'string'})
    }

    const modal = params.get('modal')
    if(modal != null) {
      $('#exportModal').modal('show')
    }

    this.parseTheme()

    const clipboard = new ClipboardJS('.copy .btn')
    $('.copy .btn').tooltip({trigger: 'click'})
    clipboard.on('success', () => {
      $('.copy .btn').tooltip('hide')
        .attr('data-original-title', "Copied!")
        .tooltip('show')
      setTimeout(() => {
        $('.copy .btn').tooltip('hide')
      }, 1000)
    })

    this.origin = window.location.origin

    window.onresize = () => {
       this.$refs.split.direction = this.direction()
       // eslint-disable-next-line
       console.log(this.direction())
    }
  },
  watch: {
    code() {
      this.parseTheme()
    }
  },
  computed: {
    runningiOS() {
      return (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || 
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    }
  },
  methods: {
    parseTheme() {
      try {
        // TODO: Add proper theme validation
        this.error = null
        this.theme = JSON.parse(this.code)

        const gzipped = pako.deflate(this.code, {to: 'string'})
        this.hash = this.urlSafeB64Encode(gzipped)
        // eslint-disable-next-line
      } catch(e) {
        this.error = e
        this.theme = null
        this.hash = null
      }
    },
    urlSafeB64Encode(str) {
      return btoa(str)
        .replace(/=/g, "")
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
    },
    urlSafeB64Decode(str) {
      return atob(this.padString(str)
        .replace(/-/g, "+")
        .replace(/_/g, "/"))
    },
    padString(input) {
      const segmentLength = 4
      let stringLength = input.length
      let diff = stringLength % segmentLength

      if (!diff) return input

      let position = stringLength
      let padLength = segmentLength - diff
      let paddedStringLength = stringLength + padLength
      let buffer = Buffer.alloc(paddedStringLength)

      buffer.write(input)

      while (padLength--) {
          buffer.write("=", position++)
      }

      return buffer.toString()
    },
    direction() {
      if (window.innerWidth <= 576) {
        return 'vertical'
      }
      return 'horizontal'
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css?family=Montserrat:400,700&display=swap');

body {
  margin: 0;
  overflow: hidden;
}

.navbar {
  box-shadow:0 0 25px rgba(17, 51, 102, 0.06), 0 20px 25px rgba(17, 51, 102, 0.06), 0 3px 4px rgba(17, 51, 102, 0.06);
  z-index: 5;
}

.navbar::after {
  content: '';
  background: rgba(255, 255, 255, 0.93);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index:-1
}

@supports (-webkit-backdrop-filter: blur(15px)) or(backdrop-filter: blur(15px)) {
  .navbar::after {
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(15px);
    background: rgba(255, 255, 255, 0.6)
  }
}

.navbar .navbar-brand {
  font-size: 1.5rem;
  font-family: Montserrat, sans-serif;
  letter-spacing: .01rem;
   transform: scale(0.94, 1) translate(-3%, 0);
  font-weight: bold !important;
  color: #313848 !important;
}

.navbar .navbar-brand .sub-title {
  margin-left: 0.5rem;
  font-weight: normal;
  font-size: 1.25rem;
  color: #6c757d;
}

.copy {
  position: relative;
}

.copy input {
  padding-right: 70px;
}

.copy .btn {
  position: absolute;
  right: 16px;
  top: 16px;
}

.vue-codemirror {
  overflow: hidden;
  height: 100%;
}

.CodeMirror {
  height: 100% !important;
  padding-bottom: 55px !important;
}

.CodeMirror-scroll {
  padding-top: 61px;
}

.split-horizontal:last-of-type {
  padding-top: 61px;
}

.theme-variant-switch .btn:not(.btn-primary) {
  background-color: #e9edf2;
}
</style>
